import React from 'react'
import { motion } from 'framer-motion'

interface LoadingSkeletonProps {
  type?: 'dashboard' | 'card' | 'list' | 'chart'
  count?: number
}

// Типы для Framer Motion
const shimmerVariants = {
  initial: { x: '-100%' },
  animate: {
    x: '200%',
    transition: {
      duration: 1.5,
      repeat: Infinity,
      ease: "linear" as const
    }
  }
}

export const LoadingSkeleton: React.FC<LoadingSkeletonProps> = ({
  type = 'dashboard',
  count = 3
}) => {
  if (type === 'dashboard') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-950 p-4 sm:p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gray-200 dark:bg-gray-800 animate-pulse" />
              <div className="space-y-2">
                <div className="w-32 h-4 bg-gray-200 dark:bg-gray-800 rounded animate-pulse" />
                <div className="w-24 h-3 bg-gray-200 dark:bg-gray-800 rounded animate-pulse" />
              </div>
            </div>
            <div className="w-24 h-10 bg-gray-200 dark:bg-gray-800 rounded-xl animate-pulse" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 mb-8">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="relative overflow-hidden rounded-2xl bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 p-6"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gray-200 dark:bg-gray-700 animate-pulse" />
                  <div className="flex-1 space-y-3">
                    <div className="w-3/4 h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                    <div className="w-1/2 h-6 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <div className="w-16 h-3 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                        <div className="w-10 h-3 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                      </div>
                      <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                        <motion.div
                          variants={shimmerVariants}
                          initial="initial"
                          animate="animate"
                          className="w-1/2 h-full bg-gradient-to-r from-transparent via-white/20 to-transparent"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              <div className="h-12 bg-gray-200 dark:bg-gray-800 rounded-2xl animate-pulse" />
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-16 bg-gray-200 dark:bg-gray-800 rounded-xl animate-pulse" />
              ))}
            </div>
            <div className="space-y-6">
              <div className="h-48 bg-gray-200 dark:bg-gray-800 rounded-2xl animate-pulse" />
              <div className="h-32 bg-gray-200 dark:bg-gray-800 rounded-2xl animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (type === 'list') {
    return (
      <div className="space-y-3">
        {[...Array(count)].map((_, i) => (
          <div
            key={i}
            className="relative overflow-hidden rounded-xl bg-gray-100 dark:bg-gray-800 p-4"
            style={{ animationDelay: `${i * 100}ms` }}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gray-300 dark:bg-gray-700 animate-pulse" />
              <div className="flex-1 space-y-2">
                <div className="w-3/4 h-4 bg-gray-300 dark:bg-gray-700 rounded animate-pulse" />
                <div className="w-1/2 h-3 bg-gray-300 dark:bg-gray-700 rounded animate-pulse" />
              </div>
              <div className="w-16 h-6 bg-gray-300 dark:bg-gray-700 rounded animate-pulse" />
            </div>
            <motion.div
              variants={shimmerVariants}
              initial="initial"
              animate="animate"
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent"
            />
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="animate-pulse">
      <div className="bg-gray-200 dark:bg-gray-800 rounded-lg h-full" />
    </div>
  )
}